import {operationKey,isPostingRequest,completeOperation} from "./OperationKeys";
import { useEffect } from "react";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { apiClient } from "./ApiClient";
import { normalizeApiError } from "./normalizeApiError";
import { authStorage } from "../auth/AuthStorage";
import { AuthEventName } from "../constants/AuthEventName";
import {
  GlobalBusyScope,
  useBusyIndicator,
} from "../contexts/BusyIndicatorContext";
import { useError } from "../contexts/ErrorContext";
import { GetTenantUrl } from "../utils/GetTenantUrl";

export default function ApiInterceptor() {
  const { showBusyIndicator, closeBusyIndicator } = useBusyIndicator();
  const { showError } = useError();

  useEffect(() => {
    const closeRequestBusyIndicator = (
      configuration: InternalAxiosRequestConfig,
    ): void => {
      if (configuration.showBusyIndicator === false) {
        return;
      }

      closeBusyIndicator(configuration.busyScope ?? GlobalBusyScope);
    };

    const requestInterceptor = apiClient.interceptors.request.use(
      async (configuration) => {
        if (configuration.showBusyIndicator !== false) {
          showBusyIndicator({
            scope: configuration.busyScope ?? GlobalBusyScope,
            message: configuration.busyMessage,
          });
        }

        const token = authStorage.getToken();

        configuration.headers["X-Tenant-Url"] = GetTenantUrl();

        if (token) {
          configuration.headers.Authorization = `Bearer ${token}`;
        }
        if (isPostingRequest(configuration.method,configuration.url) && !configuration.headers["Idempotency-Key"]) {
          const session=authStorage.getSession();
          configuration.headers["Idempotency-Key"] = await operationKey(`${session?.User.Tenant}:${session?.User.Code}`, apiClient.getUri(configuration), configuration.data);
        }
        LogRequest(configuration);

        return configuration;
      },
    );

    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => {
        closeRequestBusyIndicator(response.config);
        LogResponse(response);
        completeOperation(response.config.headers["Idempotency-Key"] as string | undefined);
        if (/\/(save|update|delete)[^/]*$|\/(CreateReturn|ApproveOrRejectPurchaseReturn|ApproveOrRejectSaleReturn|PostInventoryCount|PostInventoryTransfer)\//i.test(response.config.url ?? ""))
          window.dispatchEvent(new Event("pantrystack:data-changed"));

        return response;
      },
      (error: unknown) => {
        LogResponseError(error);

        const configuration = getErrorConfiguration(error);

        if (configuration) {
          closeRequestBusyIndicator(configuration);
        }

        const normalizedError = normalizeApiError(error);

        if (normalizedError.status && normalizedError.status >= 400 && normalizedError.status < 500 && normalizedError.status !== 409) completeOperation(configuration?.headers["Idempotency-Key"] as string | undefined);
        if (normalizedError.status === 401) {
          authStorage.clearSession();
          window.dispatchEvent(new Event(AuthEventName.Unauthorized));
        }

        if (configuration?.showError !== false) {
          showError(normalizedError);
        }
        return Promise.reject(normalizedError);
      },
    );

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [showBusyIndicator, closeBusyIndicator, showError]);

  return null;
}

function getErrorConfiguration(
  error: unknown,
): InternalAxiosRequestConfig | undefined {
  if (typeof error !== "object" || error === null || !("config" in error)) {
    return undefined;
  }

  return error.config as InternalAxiosRequestConfig;
}
function LogResponse(response: {
  status: number;
  statusText: string;
  data: unknown;
  headers: unknown;
  config: InternalAxiosRequestConfig;
}): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const fullUrl = apiClient.getUri(response.config);

  console.groupCollapsed(
    `%c[API RESPONSE] ${response.status} ${fullUrl}`,
    "color: #059669; font-weight: bold;",
  );

  console.log("Status:", response.status);

  console.log("Status text:", response.statusText);

  console.log("Full URL:", fullUrl);

  console.log("Response headers:", response.headers);

  console.log("Response data:", response.data);

  console.groupEnd();
}
function LogRequest(configuration: InternalAxiosRequestConfig): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const fullUrl = apiClient.getUri(configuration);

  console.groupCollapsed(
    `%c[API REQUEST] ${configuration.method?.toUpperCase()} ${fullUrl}`,
    "color: #2563eb; font-weight: bold;",
  );

  console.log("Method:", configuration.method?.toUpperCase());

  console.log("Base URL:", configuration.baseURL);

  console.log("Endpoint:", configuration.url);

  console.log("Full URL:", fullUrl);

  console.log("Query parameters:", configuration.params);

  console.log("Headers:", SanitizeHeaders(configuration.headers));

  console.log("Payload:", SanitizePayload(configuration.data));



  console.groupEnd();
}
function LogResponseError(error: unknown): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const axiosError = error as AxiosError;

  const configuration = axiosError.config;

  const fullUrl = configuration ? apiClient.getUri(configuration) : undefined;

  console.groupCollapsed(
    `%c[API ERROR] ${axiosError.code ?? "UNKNOWN"} ${fullUrl ?? ""}`,
    "color: #dc2626; font-weight: bold;",
  );

  console.log("Message:", axiosError.message);

  console.log("Code:", axiosError.code);

  console.log("Status:", axiosError.response?.status);

  console.log("Base URL:", configuration?.baseURL);

  console.log("Endpoint:", configuration?.url);

  console.log("Full URL:", fullUrl);

  console.log("Response data:", axiosError.response?.data);

  console.log("Error:", error);

  console.groupEnd();
}
function SanitizeHeaders(
  headers: InternalAxiosRequestConfig["headers"],
): Record<string, unknown> {
  const safeHeaders = {
    ...headers,
  } as Record<string, unknown>;

  if (safeHeaders.Authorization) {
    safeHeaders.Authorization = "[REDACTED]";
  }

  return safeHeaders;
}
function SanitizePayload(data: unknown): unknown {
  if (!data) {
    return data;
  }

  if (data instanceof FormData) {
    return Array.from(data.entries()).map(([key, value]) => [
      key,
      key.toLowerCase().includes("password") ? "[REDACTED]" : value,
    ]);
  }

  if (typeof data === "string") {
    try {
      return SanitizeObject(JSON.parse(data) as Record<string, unknown>);
    } catch {
      return data;
    }
  }

  if (typeof data === "object") {
    return SanitizeObject(data as Record<string, unknown>);
  }

  return data;
}
function SanitizeObject(
  value: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).map(([key, propertyValue]) => [
      key,
      key.toLowerCase().includes("password") ? "[REDACTED]" : propertyValue,
    ]),
  );
}
